import { CadNextNotificationPriorityEnum } from '@mark43/cad-api';
import {
    useAllEventNotificationsForEntityTypeForEvent,
    useEventNotificationsStore,
} from './use-event-notifications';
import { useEventNotificationsEventId } from './event-notifications-event-id-context';
import {
    AdditionalInfoNotificationManager,
    AdditionalInfoView,
    Priority,
    UnreadInfo,
} from '../additional-infos/types';
import { sleep } from '@m43/core';
import { MARK_AS_READ_DELAY_MS } from '../additional-infos/hooks/use-additional-infos/use-additional-infos';

export function useCadNextAdditionalInfoNotifications(): AdditionalInfoNotificationManager {
    const linkedEntityId = useEventNotificationsEventId();

    const notifs = useAllEventNotificationsForEntityTypeForEvent({
        eventId: linkedEntityId,
        entityType: 'ADDITIONAL_INFO',
    });

    const sortedNotifs = [...notifs].sort((n1, n2) => n1.entityId - n2.entityId);
    const store = useEventNotificationsStore();

    const getUnreadInfoForPriority = (priority: Priority): UnreadInfo | null => {
        const isHighPriority = priority === 'HIGH_PRIORITY_UPDATE';
        const priorityValue = isHighPriority
            ? CadNextNotificationPriorityEnum.HIGH
            : CadNextNotificationPriorityEnum.STANDARD;

        const unreadNotification = sortedNotifs.find(
            (n) => n.notificationPriority === priorityValue.name
        );

        return unreadNotification ? { id: unreadNotification.entityId, priority } : null;
    };

    const unreadInfos = {
        [Priority.STANDARD]: getUnreadInfoForPriority(Priority.STANDARD),
        [Priority.HIGH]: getUnreadInfoForPriority(Priority.HIGH),
    };

    const earliestUnreadInfoId = sortedNotifs[0]?.entityId;

    const syncUnreadPriority = (id: number, priority: UnreadInfo['priority']): void => {
        if (!linkedEntityId) {
            return;
        }
        const existing = notifs.find((n) => n.entityId === id);
        if (!existing) {
            return;
        }
        const nextPriority =
            priority === Priority.HIGH
                ? CadNextNotificationPriorityEnum.HIGH.name
                : CadNextNotificationPriorityEnum.STANDARD.name;

        if (existing.notificationPriority === nextPriority) {
            return;
        }
        store.setNotification({ ...existing, notificationPriority: nextPriority });
    };

    const hasUnread = notifs.length !== 0;

    // Remove the ADDITIONAL_INFO notifications whose entityId (= narrative row
    // id) is in `entityIds`. Reads the store fresh at call time rather than
    // the render-time `notifs` capture, so a notification that arrived after
    // this render (e.g. during markAsRead's delay) is removed too.
    const removeNotificationsForEntityIds = (entityIds: Set<number>): void => {
        if (!linkedEntityId || entityIds.size === 0) {
            return;
        }
        store
            .getAllNotificationsForEntityTypeForEvent(linkedEntityId, 'ADDITIONAL_INFO')
            .filter((notification) => entityIds.has(notification.entityId))
            .forEach((notification) => {
                store.removeNotifications({
                    eventId: linkedEntityId,
                    entityId: notification.entityId,
                    notifications: [notification.notificationType],
                });
            });
    };

    // Dismiss only the notifications whose rows the user has actually seen —
    // `infos` is the set of rows visible in the viewport (see
    // useUpdateVisibleAsRead). Notifications for out-of-view rows must survive
    // so the unread indicator persists until the user scrolls them into view.
    const markAsRead = async (infos: AdditionalInfoView[], immediate = false): Promise<void> => {
        if (!immediate) {
            await sleep(MARK_AS_READ_DELAY_MS);
        }
        removeNotificationsForEntityIds(new Set(infos.map((info) => info.id)));
    };

    // Drop notifications for rows that no longer exist (deleted via websocket
    // or user action). Their rows can never be "in view", so without this the
    // indicator would be undismissable.
    const discardNotifications = (ids: number[]): void => {
        removeNotificationsForEntityIds(new Set(ids));
    };

    return {
        hasUnread,
        unreadInfos,
        markAsRead,
        syncUnreadPriority,
        discardNotifications,
        earliestUnreadInfo: earliestUnreadInfoId ? { id: earliestUnreadInfoId } : null,
    };
}
